import { Megaphone } from 'lucide-react';
import { formatDMY } from '../../utils/dateFormat';
import {
  ANNOUNCEMENT_TONES, MAX_TEXT, SECTION, announcementStatus, brusselsToday,
} from './siteHeaderModel';
import { ANNOUNCEMENT_TONE_STYLES } from './headerDefaults';
import {
  BilingualInput, LinkField, SectionCard, StatusChip, SwitchRow, TextField,
} from './editorControls';
import styles from './HeaderEditor.module.css';

const STATUS = {
  off: { tone: 'muted', label: 'Off' },
  empty: { tone: 'warn', label: 'No text yet' },
  scheduled: { tone: 'blue', label: 'Scheduled' },
  ended: { tone: 'muted', label: 'Ended' },
  live: { tone: 'green', label: 'Live now' },
};

/** Where the bar stands today, for the card header and the preview. */
export function AnnouncementStatusChip({ announcement, today = brusselsToday() }) {
  const status = announcementStatus(announcement, today);
  const s = STATUS[status];
  let title = '';
  if (status === 'scheduled') title = `Starts ${formatDMY(announcement.startDate, '')}`;
  if (status === 'ended') title = `Ended ${formatDMY(announcement.endDate, '')}`;
  return <StatusChip tone={s.tone} title={title}>{s.label}</StatusChip>;
}

/** A sentence about the date window, dd/mm/yyyy. */
export function windowSentence(a) {
  const from = formatDMY(a.startDate, '');
  const to = formatDMY(a.endDate, '');
  if (from && to) return `Shows from ${from} up to and including ${to}.`;
  if (from) return `Shows from ${from}, with no end date.`;
  if (to) return `Shows now, up to and including ${to}.`;
  return 'No dates: shows for as long as it is switched on.';
}

/**
 * The thin strip above the header: a short message with an optional link. It reaches visitors
 * only while it is switched on, has text and today (Brussels time) is inside its dates.
 */
export default function AnnouncementSection({ value, onPatch, errorCount }) {
  const a = value;
  return (
    <SectionCard
      id={SECTION.announcement}
      icon={Megaphone}
      tone="orange"
      title="Announcement bar"
      description="A thin strip above the header for a sale, a notice or news."
      enabled={a.enabled}
      onToggle={(enabled) => onPatch({ enabled })}
      showOn={a.showOn}
      onShowOn={(showOn) => onPatch({ showOn })}
      status={<AnnouncementStatusChip announcement={a} />}
      errorCount={errorCount}
      previewTarget="announcement"
    >
      <BilingualInput
        label="Message"
        value={a.text}
        maxLength={MAX_TEXT}
        placeholder="e.g. Autumn sale: up to 15% off"
        errorPath="announcement.text"
        required={a.enabled}
        onChange={(text) => onPatch({ text })}
        hint={`Up to ${MAX_TEXT} characters per language.`}
      />

      <div className={styles.grid2}>
        <BilingualInput
          label="Link text"
          value={a.linkLabel}
          placeholder="e.g. See deals"
          onChange={(linkLabel) => onPatch({ linkLabel })}
        />
        <LinkField
          value={a.link}
          placeholder="/results?entry=last_minute"
          errorPath="announcement.link"
          newTab={a.newTab}
          onChange={(link) => onPatch({ link })}
          onNewTab={(newTab) => onPatch({ newTab })}
        />
      </div>

      <div className={styles.grid2}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Colour</label>
          <div className={styles.toneRow} role="group" aria-label="Bar colour">
            {ANNOUNCEMENT_TONES.map((tone) => {
              const t = ANNOUNCEMENT_TONE_STYLES[tone];
              return (
                <button
                  key={tone}
                  type="button"
                  className={`${styles.toneSwatch} ${a.tone === tone ? styles.toneSwatchOn : ''}`}
                  style={{ background: t.background, color: t.color }}
                  aria-pressed={a.tone === tone}
                  title={t.label}
                  onClick={() => onPatch({ tone })}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
        <SwitchRow
          title="Visitors can close it"
          description="Shows a small cross so a visitor can hide the bar."
          checked={a.dismissible}
          onChange={(dismissible) => onPatch({ dismissible })}
        />
      </div>

      <div className={styles.grid3}>
        <TextField
          label="Start date"
          type="date"
          value={a.startDate}
          onChange={(startDate) => onPatch({ startDate })}
        />
        <TextField
          label="End date"
          type="date"
          value={a.endDate}
          errorPath="announcement.endDate"
          inputProps={{ min: a.startDate || undefined }}
          onChange={(endDate) => onPatch({ endDate })}
        />
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Window</label>
          <p className={styles.windowNote}>{windowSentence(a)}</p>
        </div>
      </div>
    </SectionCard>
  );
}
